import {
	HttpContextToken,
	HttpEvent,
	HttpInterceptor,
	HttpHandler,
	HttpRequest,
	HttpErrorResponse
} from "@angular/common/http";
import { from, Observable, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { Injectable } from "@angular/core";

import { ToastService } from "@shared/services/toast/toast.service";
import { environment } from "@environments/environment";
import { AuthService } from "@core/services/auth.service";

// In the request context, not a header: a custom header would change the CORS preflight
const RETRIED = new HttpContextToken(() => false);

@Injectable({ providedIn: "root" })
export class HttpErrorInterceptor implements HttpInterceptor {
	constructor(public toast: ToastService, private authService: AuthService) {}

	// Shared so concurrent 401s wait on one verification instead of each independently deciding to log out
	private verificationInFlight: Promise<boolean> | null = null;

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(request).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error.status === 401) {
					return this.handleUnauthorized(error, request, next);
				}

				this.displayError(error);
				return throwError(() => error);
			})
		);
	}

	// A 401 can be an access token that expired while the Ping session is still alive: renew once and retry once.
	// Only if that does not help is the session checked, and ended only when Ping confirms it expired.
	private handleUnauthorized(
		error: HttpErrorResponse,
		request: HttpRequest<unknown>,
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		if (error.url?.includes(environment.pingIdentity.authUrl)) {
			return throwError(() => error);
		}

		if (!request.context.get(RETRIED)) {
			return from(this.authService.renewTokens()).pipe(
				switchMap(renewed => (renewed ? this.retry(request, next) : this.verifyAndFail(error)))
			);
		}

		return this.verifyAndFail(error);
	}

	private retry(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const retried = request.clone({
			context: request.context.set(RETRIED, true),
			setHeaders: this.refreshedAuthorization(request)
		});

		return next.handle(retried).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error.status === 401) {
					return this.handleUnauthorized(error, retried, next);
				}

				this.displayError(error);
				return throwError(() => error);
			})
		);
	}

	// The auth interceptor runs before this one, so the cloned request still carries the old token
	private refreshedAuthorization(request: HttpRequest<unknown>): Record<string, string> {
		const scheme = request.headers.get("Authorization")?.split(" ")[0];
		const accessToken = this.authService.userTokens().accessToken;
		return scheme && accessToken ? { Authorization: `${scheme} ${accessToken}` } : {};
	}

	private verifyAndFail(error: HttpErrorResponse): Observable<never> {
		return from(this.verifySessionExpiry()).pipe(
			switchMap(expired => {
				if (!expired) {
					this.displayError(error);
				}
				return throwError(() => error);
			})
		);
	}

	private expireSession(): void {
		this.toast.showError("Session Timed Out");
		this.authService.logout(true);
	}

	private displayError(error: HttpErrorResponse): void {
		if (Array.isArray(error.error)) {
			this.showManyErrors(error.error);
		} else {
			this.showSingleError(error);
		}
	}

	private showManyErrors(errors: HttpErrorResponse[]): void {
		let errorsMessage = "";
		errors.forEach(singleError => {
			errorsMessage += singleError.message + ". ";
		});
		this.toast.showError(errorsMessage);
	}

	private showSingleError(error: HttpErrorResponse): Observable<never> | void {
		if (error.status === 403) {
			this.toast.showError("Your account has not been granted permissions. Please contact the administrator");
			return throwError(
				() => new Error("Your account has not been granted permissions. Please contact the administrator")
			);
		}

		if (error.status === 409) {
			console.log(error);
			this.toast.showError(error.error.message);
		}

		if (error.status === 400 && error.error.code === "TaskNotCompleted") {
			return throwError(() => error);
		}

		if (error.error?.message && error.status !== 422 && error.status !== 200) {
			if (
				error.error.message.includes("TPP service definition not found") ||
				error.error.message.includes("'TPPServiceDefinition' is not found by id")
			) {
				/* empty */
			} else {
				this.toast.showError(error.error.message);
			}
		}
	}

	private verifySessionExpiry(): Promise<boolean> {
		if (!this.verificationInFlight) {
			this.verificationInFlight = this.checkAndHandleExpiry().finally(() => (this.verificationInFlight = null));
		}
		return this.verificationInFlight;
	}

	//Single shared check so expireSession() fires at most once per expiry event, no matter how many requests are waiting
	private async checkAndHandleExpiry(): Promise<boolean> {
		try {
			const minutesToExpiry = await this.authService.testSession();
			if (minutesToExpiry <= 0) {
				this.expireSession();
				return true;
			}
			return false;
		} catch (err: any) {
			// testSession() only checks a nested error.status, so a bodyless 401 is rethrown here instead of resolving to 0
			if (err?.status === 401) {
				this.expireSession();
				return true;
			}
			console.error("Unable to verify session status after 401", err);
			return false;
		}
	}
}
